import { forwardRef, useState, useEffect } from "react";
import {
  parsePhoneNumberFromString,
  AsYouType,
  CountryCode,
} from "libphonenumber-js";
import { BaseInputProps } from "../../types";
import styles from "./PhoneInput.module.scss";

export interface PhoneInputProps extends Omit<BaseInputProps, "type"> {
  /** Default country code (ISO 3166-1 alpha-2) */
  defaultCountry?: CountryCode;
  /** Callback when phone number changes */
  onPhoneChange?: (phone: string, isValid: boolean) => void;
}

/**
 * PhoneInput Component
 *
 * International phone number input with E.164 formatting
 *
 * @example
 * ```tsx
 * <PhoneInput
 *   name="phone"
 *   label="Phone Number"
 *   defaultCountry="US"
 *   placeholder="+1 (555) 000-0000"
 *   required
 *   error={errors.phone?.message}
 *   onPhoneChange={(phone, isValid) => console.log(phone, isValid)}
 * />
 * ```
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      label,
      error,
      helperText,
      name,
      required = false,
      fullWidth = true,
      disabled,
      className,
      defaultCountry = "IN",
      onPhoneChange,
      onChange,
      value,
      ...props
    },
    ref
  ) => {
    const [displayValue, setDisplayValue] = useState("");
    const [internalValue, setInternalValue] = useState("");
    const inputId = `input-${name}`;
    const errorId = error ? `${inputId}-error` : undefined;
    const helperId = helperText ? `${inputId}-helper` : undefined;
    const hasError = Boolean(error);

    // Initialize with value prop if provided
    useEffect(() => {
      if (value && typeof value === "string") {
        setInternalValue(value);
        try {
          const phoneNumber = parsePhoneNumberFromString(value);
          if (phoneNumber) {
            setDisplayValue(phoneNumber.formatInternational());
          } else {
            setDisplayValue(value);
          }
        } catch {
          setDisplayValue(value);
        }
      }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Format as you type
      const formatter = new AsYouType(defaultCountry);
      const formatted = formatter.input(inputValue);

      setDisplayValue(formatted);

      // Get E.164 format for the actual value
      let e164Value = "";
      let isValid = false;

      try {
        const phoneNumber = parsePhoneNumberFromString(
          inputValue,
          defaultCountry
        );
        if (phoneNumber) {
          e164Value = phoneNumber.number;
          isValid = phoneNumber.isValid();
        }
      } catch {
        e164Value = inputValue;
      }

      setInternalValue(e164Value);

      // Call callbacks
      if (onPhoneChange) {
        onPhoneChange(e164Value, isValid);
      }

      if (onChange) {
        // Create a synthetic event with E.164 value
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: e164Value,
            name,
          },
        };
        onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
      }
    };

    return (
      <div
        className={`${styles.container} ${fullWidth ? styles.fullWidth : ""} ${
          className || ""
        }`}
      >
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
            {required && (
              <span className={styles.required} aria-label="required">
                *
              </span>
            )}
          </label>
        )}

        <div className={styles.inputWrapper}>
          <span className={styles.prefix}>
            <PhoneIcon />
          </span>

          <input
            ref={ref}
            id={inputId}
            name={name}
            type="tel"
            disabled={disabled}
            value={displayValue}
            onChange={handleChange}
            aria-invalid={hasError}
            aria-describedby={
              [errorId, helperId].filter(Boolean).join(" ") || undefined
            }
            aria-required={required}
            className={`${styles.input} ${hasError ? styles.inputError : ""}`}
            autoComplete="tel"
            {...props}
          />

          {/* Hidden input with E.164 value for form submission */}
          <input type="hidden" name={`${name}_e164`} value={internalValue} />
        </div>

        {error && (
          <p id={errorId} className={styles.error} role="alert">
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={helperId} className={styles.helper}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

PhoneInput.displayName = "PhoneInput";

// Phone icon
const PhoneIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);
